import { useState, useEffect, useCallback } from 'react'
import { getOptimizedImageUrl } from '../lib/utils'

export default function GalleryLightbox({ items = [], title = 'Galleri', description }) {
  const [activeIdx, setActiveIdx] = useState(null)
  const [touchStartX, setTouchStartX] = useState(null)

  const isOpen = activeIdx !== null
  const currentItem = isOpen ? items[activeIdx] : null

  const handleNext = useCallback(() => {
    if (activeIdx === null) return
    setActiveIdx((prev) => (prev + 1) % items.length)
  }, [activeIdx, items.length])

  const handlePrev = useCallback(() => {
    if (activeIdx === null) return
    setActiveIdx((prev) => (prev - 1 + items.length) % items.length)
  }, [activeIdx, items.length])

  const handleClose = useCallback(() => {
    setActiveIdx(null)
  }, [])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    function onKeyDown(e) {
      if (e.key === 'Escape') handleClose()
      if (e.key === 'ArrowRight') handleNext()
      if (e.key === 'ArrowLeft') handlePrev()
    }

    window.addEventListener('keydown', onKeyDown)
    // Prevent background scrolling when lightbox is open
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, handleNext, handlePrev, handleClose])

  // Touch swipe handling for mobile
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX)
  }

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return
    const touchEndX = e.changedTouches[0].clientX
    const diffX = touchStartX - touchEndX

    if (Math.abs(diffX) > 45) {
      if (diffX > 0) {
        handleNext()
      } else {
        handlePrev()
      }
    }
    setTouchStartX(null)
  }

  if (!items || items.length === 0) return null

  return (
    <div className="w-full">
      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item, idx) => {
          const rawImg = Array.isArray(item.image) ? item.image[0] : item.image || item.url
          return (
            <div
              key={item.id || item.slug || idx}
              onClick={() => setActiveIdx(idx)}
              className="group relative cursor-pointer overflow-hidden rounded-sm border border-white/10 bg-[#18181C] transition-all duration-300 hover:border-[#C87D46]/60 hover:shadow-xl hover:shadow-black/50 flex flex-col"
            >
              {/* Image box */}
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-black/40">
                <img
                  src={getOptimizedImageUrl(rawImg, { width: 800, quality: 85 })}
                  alt={item.title || `HiFi Galleri ${idx + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />

                {/* Hover indicator badge */}
                <div className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-[#E5E2DB] opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:scale-110 border border-white/20">
                  <i className="fa-solid fa-magnifying-glass-plus text-xs" aria-hidden="true" />
                </div>

                <div className="absolute bottom-3 left-3 text-[11px] uppercase tracking-widest text-[#C87D46] font-medium bg-black/80 px-2.5 py-1 rounded-sm border border-white/10">
                  {idx + 1} / {items.length}
                </div>
              </div>

              {/* Caption & Title box */}
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <h4 className="font-serif text-base text-[#F5F3EF] group-hover:text-[#C87D46] transition-colors font-medium">
                    {item.title}
                  </h4>
                  {item.caption && (
                    <p className="mt-2 text-xs leading-relaxed text-[#A8A49C] line-clamp-3 whitespace-pre-line">
                      {item.caption}
                    </p>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-[#C87D46]">
                  <span className="font-medium">Klik for at forstørre</span>
                  <i className="fa-solid fa-arrow-right text-[10px] transform group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Lightbox Modal */}
      {isOpen && currentItem && (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 backdrop-blur-md p-4 sm:p-6"
          onClick={handleClose}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Top bar with counter and close button */}
          <div
            className="absolute top-4 left-4 right-4 flex items-center justify-between z-20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-widest text-[#C87D46] font-mono bg-white/10 px-3 py-1 rounded-sm">
                {activeIdx + 1} / {items.length}
              </span>
              <span className="hidden sm:inline-block font-serif text-sm text-[#E5E2DB]">
                {currentItem.title}
              </span>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#C87D46] transition-colors border border-white/20"
              aria-label="Luk galleri"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Navigation Prev Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handlePrev()
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#C87D46] hover:scale-105 transition-all border border-white/20"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-base" aria-hidden="true" />
          </button>

          {/* Main Image Container */}
          <div
            className="relative max-h-[75vh] max-w-[90vw] flex flex-col items-center justify-center select-none"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getOptimizedImageUrl(
                Array.isArray(currentItem.image) ? currentItem.image[0] : currentItem.image || currentItem.url,
                { width: 1600, quality: 90 }
              )}
              alt={currentItem.title || 'HiFi lytterum billede'}
              className="max-h-[65vh] w-auto max-w-full rounded-sm object-contain shadow-2xl border border-white/10"
            />

            {/* Lightbox Caption Box */}
            <div className="mt-4 max-w-2xl text-center bg-[#141418]/90 border border-white/10 p-4 rounded-sm">
              <h3 className="font-serif text-lg text-[#F5F3EF]">
                {currentItem.title}
              </h3>
              {currentItem.caption && (
                <p className="mt-1 text-xs sm:text-sm text-[#E5E2DB] whitespace-pre-line leading-relaxed">
                  {currentItem.caption}
                </p>
              )}
            </div>
          </div>

          {/* Navigation Next Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleNext()
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#C87D46] hover:scale-105 transition-all border border-white/20"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-base" aria-hidden="true" />
          </button>

          {/* Bottom thumbnails on desktop */}
          <div
            className="hidden md:flex items-center gap-3 absolute bottom-4 z-20"
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((thumb, tIdx) => {
              const thumbImg = Array.isArray(thumb.image) ? thumb.image[0] : thumb.image || thumb.url
              return (
                <button
                  key={thumb.id || tIdx}
                  type="button"
                  onClick={() => setActiveIdx(tIdx)}
                  className={`relative h-14 w-20 overflow-hidden rounded-sm border transition-all ${
                    tIdx === activeIdx
                      ? 'border-[#C87D46] ring-2 ring-[#C87D46]/50 scale-105'
                      : 'border-white/20 opacity-50 hover:opacity-100'
                  }`}
                  aria-label={`Gå til billede ${tIdx + 1}`}
                >
                  <img
                    src={getOptimizedImageUrl(thumbImg, { width: 160, quality: 75 })}
                    alt={thumb.title || ''}
                    className="h-full w-full object-cover"
                  />
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
